/**
 * Created by vladislav on 2/22/2024
 */

const PaymentsLogic = function () {
    connector.payments.on("productsLoaded", this.onProductsLoaded.bind(this));
    connector.payments.on("restore", this.onRestore.bind(this));
    connector.payments.on("changeStatus", this.updateFlags.bind(this));
    connector.payments.on("error:purchase", this.onPurchaseError.bind(this));
    connector.payments.on("error:unknownProducts", this.onUnknownProductsError.bind(this));
    connector.payments.on("error:unknownSubscriptions", this.onUnknownSubscriptionsError.bind(this));

    PaymentOverlay.initialize();
    connector.payments.on("pluginChanged", () => {
        this.setPendingPurchaseToRestore(undefined);

        PaymentOverlay.initialize();
    });

    cleverapps.gameManager.on("resume", () => connector.payments.loadProductsThrottled());
};

PaymentsLogic.prototype.updateFlags = function () {
    cleverapps.whenAllInitialized(() => {
        cleverapps.flags.update();
    });
};

PaymentsLogic.prototype.onProductsLoaded = function () {
    connector.payments.products.forEach((product) => {
        if (product.currencyImage) {
            Product.RegisterCurrencyImage(product.currency, product.currencyImage);
        }
    });
};

PaymentsLogic.prototype.onChangeStatus = function (callback, target) {
    const listener = connector.payments.on("changeStatus", callback);

    if (target) {
        addCleaner(target, listener.clear);
    }
};

PaymentsLogic.prototype.createPurchaseContext = function (product) {
    const context = product.context || {};
    const restorable = this.getRestoreLogic(product, context);
    const reward = restorable.getBoughtReward(context);

    if (RewardsList.isEmpty(reward)) {
        return product.context;
    }

    return {
        ...context,
        reward,
        location: restorable.requiredLocation(product, context)
    };
};

PaymentsLogic.prototype.purchase = function (product, callback, options) {
    if (this.purchasePlayer && this.purchasePlayer.isRunning()) {
        this.onPurchaseError("purchase_already_open");
        callback(false);
        return;
    }

    let purchase;
    let email;
    this.purchasePlayer = new ActionPlayer([
        function (f, stop) {
            if (connector.payments.isLoginRequired) {
                cleverapps.SocialManager.enforceLogin((code) => {
                    if (code === cleverapps.CODE_SUCCEED) {
                        f();
                    } else {
                        stop("login_failed");
                    }
                }, {
                    strict: true,
                    signInWindow: "purchase"
                });
            } else {
                f();
            }
        },

        function (f, stop) {
            if (connector.SYNC_STATUS.PULL === connector.synchronizer.status) {
                stop();
            } else {
                f();
            }
        },

        function (f, stop) {
            if (connector.payments.isEmailRequired) {
                this.getEmail((receivedEmail) => {
                    if (receivedEmail) {
                        email = receivedEmail;

                        f();
                    } else {
                        stop("email_required");
                    }
                });
            } else {
                f();
            }
        }.bind(this),

        function (f) {
            cleverapps.metrics.logEvent(cleverapps.EVENTS.STATS.PRODUCT.OPEN);

            cleverapps.abExperiments.allLogEvent(cleverapps.EVENTS.STATS.PRODUCT.OPEN);
            if (connector.social.isLoggedIn()) {
                cleverapps.abExperiments.allLogEvent(`${cleverapps.EVENTS.STATS.PRODUCT.OPEN}_logged_in`);
            }

            this.createOverlay();

            f();
        }.bind(this),

        function (f, stop) {
            connector.payments.purchaseCallback({
                id: product.itemId,
                context: this.createPurchaseContext(product),
                email,
                testRestore: cleverapps.config.testRestore
            }, (result) => {
                if (result) {
                    purchase = result.purchase;

                    f();
                } else {
                    stop();
                }
            });
        }.bind(this)
    ]);

    this.purchasePlayer.onComplete(() => {
        this.removeOverlay();
    });

    this.purchasePlayer.onFailure((code) => {
        console.log("PaymentsManager purchase failure", product.productId, product.itemId);

        if (code) {
            this.onPurchaseError(code);
        }

        callback(false);
    });

    this.purchasePlayer.onSuccess(() => {
        console.log("PaymentsManager purchase success", product.productId, product.itemId);

        // the reward would not be saved: the receipt stays unconsumed and the restore after the reload delivers it
        if (!cleverapps.playInTwoTabs.isSessionAlive()) {
            callback(false);
            return;
        }

        cleverapps.metrics.logEvent(cleverapps.EVENTS.STATS.PRODUCT.SUCCESS);
        cleverapps.abExperiments.allLogEvent(cleverapps.EVENTS.STATS.PRODUCT.SUCCESS);

        this.logPurchase(product, purchase);

        this.consumeWithEvents(purchase, product.itemId);

        const reward = product.reward;

        product.showBoughtMessage(reward);

        this.giveReward(reward, options);
        callback(true);
    });

    this.purchasePlayer.play();
};

PaymentsLogic.prototype.purchaseVideoProduct = function (product, callback, options) {
    cleverapps.rewardedAdsManager.playRewarded(product.getAdsLimit(), () => {
        const progress = product.getVideoProgress() + 1;

        if (progress < product.price) {
            product.saveVideoProgress(progress);
            callback(false);
            return;
        }

        product.saveVideoProgress(0);

        cleverapps.metrics.logEvent(cleverapps.EVENTS.STATS.VIDEO_PRODUCT_BUY, { itemId: product.itemId });

        this.giveReward(product.reward, { ...options, type: Product.TYPE_ADS });
        callback(true);
    }, () => callback(false), () => this.purchaseVideoProduct(product, callback, options));
};

PaymentsLogic.prototype.giveReward = function (reward, options) {
    cleverapps.user.incProgressCompare(50);
    cleverapps.effects.fire("ui.shop.buy");

    options = options || {};

    if (reward && !options.noRewardWindow) { // addMoves, subscription, growthFund
        cleverapps.focusManager.display({
            focus: "purchaseRewardWindow",
            stack: true,
            action: (f) => {
                if (options.type === Product.TYPE_ADS) {
                    RewardWindow.createAdsRewardWindow(reward);
                } else {
                    RewardWindow.createPurchaseWindow(reward);
                }
                cleverapps.focusManager.onceNoWindowsListener = f;
            }
        });
    }
};

PaymentsLogic.prototype.onPurchaseError = function (code) {
    if (code === "purchase_canceled_by_user") {
        cleverapps.notification.create(Messages.get("PurchaseError.cancelled"));
        return;
    }

    if (code === "invalid_email") {
        cleverapps.info.setKeyValue("email", "");
    }

    if (this.overlay && this.overlay.terminated) {
        code = "purchase_terminated";
    }

    const entry = PaymentsLogic.ERROR_WINDOWS[code];
    if (!entry) {
        cleverapps.notification.create(Messages.get(`PurchaseError.${code}`));
        return;
    }

    cleverapps.focusManager.display({
        focus: "PurchaseError",
        stack: true,
        action: (f) => {
            new AlertWindow({
                icons: "purchase_error",
                icon: entry.icon,
                title: entry.title,
                text: entry.noLateText && !connector.payments.canDeliverLate() ? entry.noLateText : entry.text,
                button: "OK"
            });
            cleverapps.focusManager.onceNoWindowsListener = f;
        }
    });
};

PaymentsLogic.prototype.onUnknownProductsError = function (products) {
    if (new Date(cleverapps.config.buildTime).getTime() + PaymentsLogic.UNKNOWN_PRODUCTS_ERRORS_PERIOD > Date.now() && !this.unkonwnProductsLogged) {
        this.unkonwnProductsLogged = true;

        this.logMissingProducts(products);
    } else {
        console.error(`platform missed some products ${products}`);
    }
};

PaymentsLogic.prototype.onUnknownSubscriptionsError = function (products) {
    if (new Date(cleverapps.config.buildTime).getTime() + PaymentsLogic.UNKNOWN_PRODUCTS_ERRORS_PERIOD > Date.now() && !this.unkonwnSubscriptionsLogged) {
        this.unkonwnSubscriptionsLogged = true;

        this.logMissingProducts(products);
    } else {
        console.error(`platform missed some products ${products}`);
    }
};

PaymentsLogic.prototype.logMissingProducts = function (products) {
    products.forEach((productId) => {
        const product = connector.payments.getProduct(productId);

        cleverapps.metrics.logEvent(cleverapps.EVENTS.STATS.MISSING_PRODUCTS, {
            productId: product ? product.itemId : productId
        });
    });
};

PaymentsLogic.prototype.getEmail = function (callback) {
    const email = cleverapps.info.getValue("email");

    if (email && cleverapps.validateEmail(email)) {
        callback(email);
    } else {
        cleverapps.focusManager.display({
            focus: "EmailWindow",
            stack: true,
            action: (f) => {
                let result;
                new EmailWindow((receivedEmail) => {
                    result = receivedEmail;
                });
                cleverapps.focusManager.onceNoWindowsListener = () => {
                    f();
                    callback(result);
                };
            }
        });
    }
};

PaymentsLogic.prototype.createOverlay = function () {
    cleverapps.focusManager.display({
        stack: true,
        focus: "PaymentOverlay",
        keepControls: true,
        action: function (f) {
            this.overlay = new PaymentOverlay();
            cleverapps.Scenes.GetRunningScene().addChild(this.overlay);

            this.oncePaymentOverlayClosed = f;
        }.bind(this)
    });
};

PaymentsLogic.prototype.removeOverlay = function () {
    if (this.overlay) {
        this.overlay.removeFromParent();
        this.overlay = undefined;
    }

    if (this.oncePaymentOverlayClosed) {
        this.oncePaymentOverlayClosed();
        this.oncePaymentOverlayClosed = undefined;
    }
};

PaymentsLogic.prototype.onRestore = function (purchase) {
    // the store handed a receipt back: logged before anything can drop it, with the seller type the receipt still carries
    // (no_context: the pending context is gone, the default restore will give the product's fallback reward)
    const context = connector.payments.getPaymentContext(purchase) || {};
    cleverapps.metrics.logEvent(cleverapps.EVENTS.STATS.PRODUCT.RESTORE_RECEIVED, {
        productId: purchase.productId,
        itemId: context.type || "no_context",
        details: { paymentId: purchase.paymentId, context: PaymentsLogic.contextForLog(context), pendingMatch: purchase.pendingMatch }
    });

    if (!purchase.productId || !connector.payments.getProduct(purchase.productId)) {
        return;
    }

    this.setPendingPurchaseToRestore(purchase);

    if (!cleverapps.focusManager.isFocused()) {
        this.showRestore();
    }

    // still waiting - for a free focus, the end of a sync or the right scene - so the player hears it is on its way
    if (this.pendingPurchaseToRestore) {
        cleverapps.notification.create(Messages.get("PurchaseRestore.pending"));
    }
};

// the menubar marks the shop while a receipt waits
PaymentsLogic.prototype.setPendingPurchaseToRestore = function (purchase) {
    if (purchase) {
        this.pendingPurchaseToRestore = purchase;
    } else {
        delete this.pendingPurchaseToRestore;
    }

    cleverapps.eventBus.trigger("changePendingRestore");
};

PaymentsLogic.prototype.consumeRestored = function () {
    const purchase = this.pendingPurchaseToRestore;
    if (!purchase) {
        return;
    }

    cleverapps.user.incProgressCompare(50);

    this.setPendingPurchaseToRestore(undefined);

    const catalogProduct = connector.payments.getProduct(purchase.productId);
    this.consumeWithEvents(purchase, catalogProduct ? catalogProduct.itemId : purchase.productId);
};

// the consume start / success / failure events name the product and the receipt, so the CRM can tell which purchase closed
PaymentsLogic.prototype.consumeWithEvents = function (purchase, itemId) {
    const params = {
        productId: itemId,
        details: { paymentId: purchase.paymentId, context: PaymentsLogic.contextForLog(purchase.context) }
    };

    cleverapps.metrics.logEvent(cleverapps.EVENTS.STATS.PRODUCT.CONSUME_START, params);

    connector.payments.consumeCallback(purchase, (result) => {
        cleverapps.metrics.logEvent(result
            ? cleverapps.EVENTS.STATS.PRODUCT.CONSUME_SUCCESS
            : cleverapps.EVENTS.STATS.PRODUCT.CONSUME_FAILURE, params);
    });
};

PaymentsLogic.prototype.getRestoreLogic = function (product, context) {
    // the product id matches receipts stored by older builds without a context, the growth fund
    return Restorable.BY_TYPE[context.type] || Restorable.BY_TYPE[product.itemId] || Restorable.BY_TYPE.default;
};

PaymentsLogic.prototype.isSyncRunning = function () {
    const status = connector.synchronizer.status;

    if (connector.SYNC_STATUS.INIT === status) {
        return connector.synchronizer.started;
    }

    return [connector.SYNC_STATUS.CONFLICT, connector.SYNC_STATUS.PULL, connector.SYNC_STATUS.PUSH, connector.SYNC_STATUS.WAIT].includes(status);
};

PaymentsLogic.prototype.showRestore = function () {
    const purchase = this.pendingPurchaseToRestore;

    if (!cleverapps.playInTwoTabs.isSessionAlive()) {
        return;
    }

    const purchaseContext = connector.payments.getPaymentContext(purchase);
    if (!purchaseContext && this.isSyncRunning()) {
        return;
    }

    const catalogProduct = connector.payments.getProduct(purchase.productId);
    const context = purchaseContext || {};
    const product = Product.CreateById(catalogProduct.itemId, context);

    // the outcome names what the player got, the seller's type goes to details: one axis per column
    const consume = (outcome, reward) => {
        this.consumeRestored();

        cleverapps.metrics.logEvent(cleverapps.EVENTS.STATS.PRODUCT.RESTORE, {
            productId: product.itemId,
            itemId: outcome,
            details: {
                type: context.type, paymentId: purchase.paymentId, context: PaymentsLogic.contextForLog(context), reward
            }
        });
    };

    // a receipt this account consumed on another device and the store served again: close it, give nothing
    if (connector.payments.isConsumed(purchase)) {
        consume("already_consumed");
        return;
    }

    const restorable = this.getRestoreLogic(product, context);
    if (restorable.waitGoodMoment(product, context)) {
        if (cleverapps.environment.isMainScene()) {
            this.travelToRestore(restorable.pendingLocation(product, context));
        }
        return;
    }

    // a real receipt: consume it, tell the player as a purchase does, count the payment
    const settle = (outcome, reward) => {
        consume(outcome, reward);

        product.showBoughtMessage(context.reward || product.restoreReward || product.reward);

        this.logPurchase(product, purchase);
    };

    const delivered = () => {
        cleverapps.eventBus.trigger("restore_purchase");
    };

    // the sales still grant their goods from their windows and keep the previous hook
    let actions = restorable.listActions(product, context, (source) => {
        settle(source || "given");
        if (!source) {
            delivered();
        }
    });

    if (!actions) {
        const resolved = Restorable.resolveOutcome(restorable, product, context);

        actions = [(f) => {
            settle(resolved.outcome, resolved.reward);

            if (resolved.entity) {
                restorable.give(resolved.entity, context);
                delivered();
                f();
            } else if (resolved.reward) {
                RewardWindow.createRestoredWindow(resolved.reward);
                cleverapps.focusManager.onceNoWindowsListener = f;
                delivered();
            } else {
                f();
            }
        }];
    }

    cleverapps.focusManager.display({
        focus: "restorePurchase",
        actions
    });
};

// a receipt bound to another location is not left waiting: tell the player and take them there,
// the restore placement finishes the delivery on arrival
PaymentsLogic.prototype.travelToRestore = function (location) {
    if (!location) {
        return;
    }

    cleverapps.focusManager.display({
        focus: "restoreTravel",
        action: (f) => {
            cleverapps.notification.create(Messages.get("PurchaseRestoredWindow.title"));

            cleverapps.timeouts.setTimeout(() => {
                cleverapps.meta.switchLocation(location);
                cleverapps.meta.gotoLocationScene(f);
            }, PaymentsLogic.RESTORE_TRAVEL_DELAY);
        }
    });
};

// the toast stays three seconds, the travel starts as it goes
PaymentsLogic.RESTORE_TRAVEL_DELAY = 3000;

// the restored window on top of the restore focus, for handlers that used to open a bare purchase window
PaymentsLogic.prototype.showRestoredWindow = function (reward) {
    Restorable.displayWindow("purchaseRewardWindow", () => RewardWindow.createRestoredWindow(reward));
};

PaymentsLogic.prototype.logPurchase = function (product, purchase) {
    const buyEvent = product.getBuyEventFromContext();

    const info = {
        productId: product.itemId,
        price: purchase.realPrice,
        textPrice: `${purchase.price} ${purchase.currency}`,
        paymentId: purchase.paymentId,
        itemId: buyEvent,
        method: purchase.method,
        details: {
            context: PaymentsLogic.contextForLog(purchase.context || product.context),
            environment: purchase.payload && purchase.payload.environment,
            storeUserId: purchase.payload && purchase.payload.storeUserId
        }
    };

    if (info.price === undefined || isNaN(info.price)) {
        cleverapps.throwAsync(`Purchase error: invalid price - ${JSON.stringify(info)}`);
    }

    if (product.isSubscription()) {
        cleverapps.metrics.logEvent(cleverapps.EVENTS.SUBSCRIPTION_BOUGHT, info);
        return;
    }

    cleverapps.metrics.logEvent(cleverapps.EVENTS.STATS.PAYMENTS, info);

    const params = {
        value: info.price,
        productId: product.itemId
    };

    connector.conversions.trackEvent(`${cleverapps.EVENTS.CONVERSIONS.BUY}_${product.itemId}`, {
        value: info.price,
        loggers: ["adjust", "metrics"]
    });

    const daysSinceRegistration = cleverapps.user.getDaysSinceRegistration();

    for (const days of [1, 3, 7, 30]) {
        if (daysSinceRegistration < days) {
            cleverapps.abExperiments.allLogEvent(cleverapps.EVENTS.STATS.LTV + days, { ...params, event: cleverapps.user.registered });
        }
    }

    const event = product.getSpentEventFromContext({ compact: true }) || "";
    cleverapps.abExperiments.allLogEvent(cleverapps.EVENTS.STATS.PAYMENTS, {
        ...params,
        itemId: event.substring(0, 40)
    });

    if (connector.social.isLoggedIn()) {
        cleverapps.abExperiments.allLogEvent(`${cleverapps.EVENTS.STATS.PAYMENTS}_logged_in`, params);
    }

    cleverapps.paymentsHistory.addPayment(product);

    cleverapps.kpi.logPurchase(purchase.realPrice);
};

// what the purchase was for, as the stat events and the CRM see it: the seller's context without the stashed reward
PaymentsLogic.contextForLog = function (context) {
    if (!context || Object.keys(context).length === 0) {
        return undefined;
    }
    const compact = { ...context };
    delete compact.reward;
    return compact;
};

PaymentsLogic.UNKNOWN_PRODUCTS_ERRORS_PERIOD = cleverapps.parseInterval("3 month");
PaymentsLogic.ERROR_WINDOWS = {
    not_connected: {
        icon: "not_connected",
        title: "PurchaseError.NotConnectedTitle",
        text: "PurchaseError.NotConnected"
    },
    product_not_found: {
        icon: "not_found",
        title: "PurchaseError.NotFoundTitle",
        text: "PurchaseError.NotFound"
    },
    purchase_already_open: {
        icon: "in_progress",
        title: "PurchaseError.AlreadyOpenTitle",
        text: "PurchaseError.AlreadyOpen"
    },
    email_required: {
        icon: "email",
        title: "PurchaseError.EmailRequiredTitle",
        text: "PurchaseError.EmailRequired"
    },
    invalid_email: {
        icon: "email",
        title: "PurchaseError.InvalidEmailTitle",
        text: "PurchaseError.InvalidEmail"
    },
    purchase_insufficient_funds: {
        icon: "no_funds",
        title: "PurchaseError.InsufficientFundsTitle",
        text: "PurchaseError.InsufficientFunds"
    },
    purchase_unknown_error: {
        icon: "failed",
        title: "PurchaseError.FailedTitle",
        text: "PurchaseError.Unknown",
        // where a failed purchase never arrives later, the window does not promise it will
        noLateText: "PurchaseError.UnknownContactSupport"
    },
    purchase_terminated: {
        icon: "in_progress",
        title: "PurchaseError.TerminatedTitle",
        text: "PurchaseError.Terminated"
    },
    purchase_pending: {
        icon: "in_progress",
        title: "PurchaseError.PendingTitle",
        text: "PurchaseError.Pending"
    },
    purchase_timeout: {
        icon: "timeout",
        title: "PurchaseError.TimeoutTitle",
        text: "PurchaseError.Timeout"
    },
    1: {
        icon: "support",
        title: "PurchaseError.FailedTitle",
        text: "PurchaseError.ContactSupport"
    },
    2: {
        icon: "support",
        title: "PurchaseError.FailedTitle",
        text: "PurchaseError.ContactSupport"
    }
};
